import type { Song, MediaItem } from './types';

export class SongModel {
  static fromJson(json: any): Song {
    // Better field extraction with proper fallbacks
    const rawName = json.name || '';
    const title = json.title || this.cleanSongName(rawName, json.show_name) || 'Unknown Track';
    const creator = json.creator || json.artist || json.collection || 'Unknown Artist';
    const album = json.album || json.show_name || json.date || 'Unknown Album';
    const date = json.date || json.show_date || json.year || 'Unknown Date';

    return {
      songId: json.song_id || json.songId || '',
      showId: json.show_id || json.showId || '',
      length: json.length || json.duration || '',
      track: json.track || '',
      name: encodeURI(rawName),
      creator,
      album,
      title,
      collection: json.collection || '',
      date,
      year: json.year || '',
      day: json.day || '',
      month: json.month || '',
      monthDay: json.monthday || json.monthDay || '',
      yearMonth: json.yearmonth || json.yearMonth || '',
      duration: this.parseSongLength(json.length || json.duration || '0', json.collection || ''),
      poweredBy: this.parsePoweredByFromCollection(json.collection || ''),
      showName: json.show_name || json.showName || '',
      showLink: this.parseSourceLink(json, json.collection || ''),
    };
  }

  static parseSongLength(length: string, _collection: string): number {
    if (!length) return 0;

    const arr = length.split(':');
    if (arr.length === 1) {
      const doub = parseFloat(arr[0]);
      if (isNaN(doub)) return 0;

      const dubstr = doub.toFixed(2);
      const splitter = dubstr.split('.');
      if (splitter.length === 1) {
        return parseInt(splitter[0]);
      } else {
        return parseInt(splitter[0]) * 60 + parseInt(splitter[1]) * 0.6; // Approximate seconds
      }
    } else if (arr.length === 2) {
      return parseInt(arr[0]) * 60 + parseInt(arr[1]);
    }
    return 0;
  }

  static parsePoweredByFromCollection(collection: string): string {
    // Implement based on Flutter logic
    if (collection.includes('phish')) return 'Phish.in';
    if (collection.includes('archive')) return 'Archive.org';
    return 'Live Shuffle';
  }

  static cleanSongName(name: string, showName?: string): string {
    if (!name) return name;
    if (showName) {
      const idx = name.indexOf(showName);
      if (idx !== -1) {
        const afterShow = name.substring(idx + showName.length);
        if (afterShow.startsWith('/')) {
          const filename = afterShow.substring(1);
          const decoded = decodeURIComponent(filename);
          const dotIdx = decoded.lastIndexOf('.');
          return dotIdx !== -1 ? decoded.substring(0, dotIdx) : decoded;
        }
      }
    }
    return name;
  }

  static parseSourceLink(json: any, collection: string): string {
    if (!collection) return 'https://archive.org';

    if (collection.toLowerCase() === 'phish') {
      return `https://phish.in/${json.album || ''}`;
    }

    const showName = json.show_name || json.showName;
    if (!showName) {
      return 'https://archive.org';
    }

    return `https://archive.org/details/${showName}`;
  }

  static transformSongToMediaItem(song: Song, source?: string): MediaItem {
    return {
      id: song.name,
      title: song.title || song.name,
      album: song.album || song.date,
      artist: song.creator,
      genre: song.showLink,
      duration: song.duration,
      playable: true,
      extras: {
        collection: song.collection,
        date: song.date,
        track: song.track,
        showId: song.showId,
        songId: song.songId,
        poweredBy: song.poweredBy,
        source: source || 'ls',
      },
    };
  }

  static toString(song: Song): string {
    return `Song{songId: ${song.songId}, showId: ${song.showId}, length: ${song.length}, track: ${song.track}, name: ${song.name}, creator: ${song.creator}, album: ${song.album}, title: ${song.title}, collection: ${song.collection}, date: ${song.date}, year: ${song.year}, day: ${song.day}, month: ${song.month}, monthDay: ${song.monthDay}, yearMonth: ${song.yearMonth}}`;
  }
}